import { useEffect, useState, type ReactNode } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import './session-presentation.css';
import { Button } from '@/components/ui/button';

/** Expand the existing DOM rather than mounting a second live session. */
export function SessionPresentation({ children }: { children: ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  const [videoFit, setVideoFit] = useState<'fit' | 'fill'>('fit');
  useEffect(() => {
    if (!expanded) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [expanded]);
  // Match the earlier app header layer; DOM order places us above it, while
  // later Radix portals (also z-50) can still show End/save dialogs above us.
  return (
    <div data-video-fit={videoFit} data-expanded={expanded} className={`session-presentation ${expanded ? 'fixed inset-0 z-50 overflow-y-auto bg-background p-2 md:p-3' : 'px-2 pt-2 md:px-4'}`}>
      <div className={`${expanded ? 'sticky top-0 z-10' : ''} flex flex-wrap items-center justify-end gap-2 bg-background/95 py-1`}>
        <div role="group" aria-label="Avatar framing" className="flex items-center gap-1 rounded-lg border bg-background p-1">
          <span className="px-2 text-xs text-muted-foreground">Avatar</span>
          <Button type="button" className="min-h-11" size="sm" variant={videoFit === 'fit' ? 'secondary' : 'ghost'} aria-pressed={videoFit === 'fit'} title="Show the full video without cropping" onClick={() => setVideoFit('fit')}>
            Fit
          </Button>
          <Button type="button" className="min-h-11" size="sm" variant={videoFit === 'fill' ? 'secondary' : 'ghost'} aria-pressed={videoFit === 'fill'} title="Fill the panel; edges of the video may be cropped" onClick={() => setVideoFit('fill')}>
            Fill
          </Button>
        </div>
        <Button className="min-h-11" variant="outline" onClick={() => setExpanded(value => !value)} aria-pressed={expanded}>
          {expanded ? <Minimize2 className="mr-2 h-4 w-4" /> : <Maximize2 className="mr-2 h-4 w-4" />}
          {expanded ? 'Exit presentation' : 'Presentation mode'}
        </Button>
      </div>
      {children}
    </div>
  );
}
